/**
 * LeadReopenButton — the Reopen action on a Lost lead (D4, web drawer;
 * 2026-09-25 Lost/reopen/points fix round, Task 5).
 *
 * Pulled out of LeadsPage.tsx's drawer terminal-actions block so it can be
 * unit tested on its own — LeadsPage is 3500+ lines and mounting the whole
 * page for every reopen scenario (manager / coordinator / refused / not-lost)
 * would drag in the leads list, kanban, dealer pickers and every other drawer
 * section along with it.
 *
 * 🔒 A coordinator has no leads of her own to reopen; she reaches this route
 * only by acting as her manager (`useActingDealerManager`, same rule as every
 * other dealer/prospect route — `resolveDealerAccess` on the backend). The
 * button stays disabled until that manager is resolved, so it never fires a
 * request with a blank `onBehalfOfUserId` that the server would 403 anyway.
 */
import { useState } from 'react';
import { RotateCcw, Loader2, AlertCircle } from 'lucide-react';
import { useActingDealerManager } from '@/pages/dealers/useActingDealerManager';
import { dealerActsOnBehalf } from '@/lib/roles';
import { useAuthStore } from '@/stores/auth';
import { reopenLead, reopenLeadErrorMessage, type LeadReopenResult } from './leadReopenApi';

export interface LeadReopenButtonProps {
  leadId: string;
  onReopened: (result: LeadReopenResult) => void;
}

export function LeadReopenButton({ leadId, onReopened }: LeadReopenButtonProps) {
  const authUser = useAuthStore((s) => s.user);
  const acting = useActingDealerManager();
  const actsOnBehalf = dealerActsOnBehalf(authUser?.role?.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // `dealer-acting-person.ts` refuses a coordinator with no resolved manager
  // before this ever reaches the server — match that here instead of letting
  // every click round-trip into a guaranteed 403.
  const blockedOnBehalf = actsOnBehalf && !acting.onBehalfOfUserId;
  const disabled = busy || blockedOnBehalf;

  const handleClick = async () => {
    if (disabled) return;
    setBusy(true);
    setError(null);
    try {
      const result = await reopenLead(leadId, {
        onBehalfOfUserId: actsOnBehalf ? acting.onBehalfOfUserId : null,
      });
      onReopened(result);
    } catch (err) {
      setError(reopenLeadErrorMessage(err) ?? 'Failed to reopen this lead.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className="flex items-center gap-1.5 rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 transition-colors"
        title="Bring this lead back from Lost"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
        Reopen lead
      </button>
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-red-400"><AlertCircle size={12} />{error}</p>
      )}
    </div>
  );
}
